/** Step 5: everything about this ticket in one place. */
import { Link } from "react-router-dom";
import { SecondaryButton } from "../../../../components/Button";
import { parseTicketModifiers, permutationCount } from "../../../tokens/variantName";
import type { StepProps } from "../TicketingSetupPage";
import { ACCESS_CONTROL_TYPE, OVERVIEW_PATH, PUBLIC_SITE_BASE, isPublicTicket, ticketHasSales } from "../types";
import { computeEventChecks, countByLevel } from "../checks";
import { describeGrants } from "../tokenPayload";
import { Card, CheckRow, Chip, StepFooter, money } from "../ui";

export const ReviewStep = ({ data, ticket, ticketId, checks }: StepProps) => {
  const t = ticket!;
  const counts = countByLevel(checks);
  const order = { error: 0, warning: 1, ok: 2 } as const;
  const sorted = [...checks].sort((a, b) => order[a.level] - order[b.level]);
  const eventProblems = computeEventChecks(data).filter((c) => c.level !== "ok");
  const groups = parseTicketModifiers(t.modifiers);
  const gates = data.gates.filter((g) => g.location_type === ACCESS_CONTROL_TYPE);
  const accepting = gates.filter((g) => (g.redeemable_tokens ?? []).some((n) => String(n).trim() === t.name.trim() || String(n).startsWith(t.name.trim() + " | ")));
  const publicUrl = data.event?.ticketing_slug ? PUBLIC_SITE_BASE + data.event.ticketing_slug : null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
      <div className="flex flex-col gap-3">
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-gray-900">Checks for "{t.name}"</div>
            <div className="text-sm text-gray-500">{counts.error} problem{counts.error === 1 ? "" : "s"} · {counts.warning} warning{counts.warning === 1 ? "" : "s"} · {counts.ok} passed</div>
          </div>
          <div>{sorted.map((c, i) => <CheckRow key={i} check={c} ticketId={ticketId} />)}</div>
        </Card>
        {eventProblems.length > 0 && (
          <Card title="Event-level items that affect this ticket too">
            <div>{eventProblems.map((c, i) => <CheckRow key={i} check={c} />)}</div>
          </Card>
        )}
        <StepFooter ticketId={ticketId} back="gates" next="overview" />
      </div>
      <Card title="Summary">
        <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-2 gap-x-3 text-sm">
          <dt className="text-gray-500">Price</dt><dd>{money(t.price)}{Number(t.tax_percentage) ? ` + ${(Number(t.tax_percentage) * 100).toFixed(2)}% tax` : ""}</dd>
          <dt className="text-gray-500">Status</dt><dd className="flex flex-wrap gap-1">{!t.is_active ? <Chip tone="gray">Inactive</Chip> : isPublicTicket(t) ? <Chip tone="green">Public</Chip> : t.cashless_top_up_item ? <Chip tone="blue">Top-up</Chip> : <Chip tone="gray">Comp only</Chip>}{ticketHasSales(t) && <Chip tone="blue">Has sales</Chip>}</dd>
          <dt className="text-gray-500">Available</dt><dd>{t.sales_limit_balance != null ? `${t.sales_limit_balance} of ${t.sales_limit ?? "?"}` : "unlimited"}{t.order_quantity_limit ? `, max ${t.order_quantity_limit} per order` : ""}</dd>
          <dt className="text-gray-500">Grants</dt><dd>{describeGrants(t)}</dd>
          <dt className="text-gray-500">Modifiers</dt><dd>{groups.length ? `${groups.map((g) => `${g.name} (${g.options.length})`).join(", ")} → ${permutationCount(groups)} ticket types` : "none"}</dd>
          <dt className="text-gray-500">Tab</dt><dd>{t.tab?.name ?? "none (default tab)"}</dd>
          <dt className="text-gray-500">Gates</dt><dd>{accepting.length ? accepting.map((g) => g.name).join(", ") : "none"}</dd>
        </dl>
        <div className="border-t border-gray-200 pt-3 flex flex-col gap-2">
          {publicUrl && <a href={publicUrl} target="_blank" rel="noopener noreferrer"><SecondaryButton>Open the public page</SecondaryButton></a>}
          <Link to={`/ticketing-items/${t.id}/edit`} className="text-sm">Full form (description, email text)</Link>
          <Link to={OVERVIEW_PATH} className="text-sm">All tickets</Link>
        </div>
      </Card>
    </div>
  );
};
